import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Chart } from 'chart.js';
import { AnalyticsService } from '../../../../services/analytics.service';
import { Subscription } from 'rxjs';
import { getLineChartConfig, PARKBOT_COLORS, initChartDefaults } from '../../../utils/chart-config';

@Component({
  selector: 'app-card-dropoff-rate',
  templateUrl: './card-dropoff-rate.component.html'
})
export class CardDropoffRateComponent implements OnInit, AfterViewInit, OnDestroy {
  dropoffData: any;
  trendDirection: number | null = null; // 1 = increasing, -1 = decreasing, 0 = stable
  chart: Chart;
  subscription: Subscription;
  
  constructor(private analyticsService: AnalyticsService) { }

  ngOnInit(): void {
    // Initialize Chart.js defaults
    initChartDefaults();
    
    this.subscription = this.analyticsService.getDropoffRate()
      .subscribe(data => {
        this.dropoffData = data;
        this.calculateTrend();
        
        if (this.chart) {
          this.updateChart();
        } else {
          setTimeout(() => {
            this.initChart();
          }, 100);
        }
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initChart();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private calculateTrend(): void {
    if (!this.dropoffData || !this.dropoffData.dailyStats) {
      this.trendDirection = null;
      return;
    }
    
    const dailyStats = this.dropoffData.dailyStats;
    const dates = Object.keys(dailyStats).sort();
    
    if (dates.length < 4) {
      this.trendDirection = null;
      return;
    }
    
    // Compare the average of the last 2 days with the average of the 2 days before that
    const lastTwoDays = dates.slice(-2);
    const previousTwoDays = dates.slice(-4, -2);
    
    const lastTwoDaysAvg = lastTwoDays.reduce((sum, date) => sum + dailyStats[date].rate, 0) / 2;
    const previousTwoDaysAvg = previousTwoDays.reduce((sum, date) => sum + dailyStats[date].rate, 0) / 2;
    
    // Calculate the difference and determine the trend
    const difference = lastTwoDaysAvg - previousTwoDaysAvg;
    
    if (Math.abs(difference) < 2) {
      this.trendDirection = 0; // Stable (less than 2% change)
    } else {
      this.trendDirection = difference > 0 ? 1 : -1; // Increasing or decreasing
    }
  }

  private initChart(): void {
    if (!this.dropoffData || !this.dropoffData.dailyStats) return;
    
    const canvas = document.getElementById('dropoff-chart') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      // Prepare data for daily drop-off rates
      const dailyStats = this.dropoffData.dailyStats;
      const dates = Object.keys(dailyStats).sort();
      
      // Get the last 14 days of data or all if less than 14 days
      const displayDates = dates.slice(-14);
      const labels = displayDates.map(date => {
        const d = new Date(date);
        return `${d.getMonth() + 1}/${d.getDate()}`;
      });
      
      const rateData = displayDates.map(date => dailyStats[date].rate);
      const incompleteData = displayDates.map(date => dailyStats[date].incomplete);
      
      // Create a threshold line dataset (constant value of 10%)
      const thresholdData = Array(displayDates.length).fill(10);
      
      const datasets = [
        {
          label: 'Drop-off Rate (%)',
          backgroundColor: 'rgba(248, 71, 95, 0.1)',
          borderColor: PARKBOT_COLORS.primary,
          data: rateData,
          fill: true,
          yAxisID: 'y'
        },
        {
          label: 'Incomplete Sessions',
          backgroundColor: PARKBOT_COLORS.warning,
          borderColor: PARKBOT_COLORS.warning,
          data: incompleteData,
          type: 'bar',
          yAxisID: 'y1'
        },
        {
          label: 'Threshold (10%)',
          borderColor: 'rgba(255, 0, 0, 0.5)',
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          pointRadius: 0,
          data: thresholdData,
          yAxisID: 'y'
        }
      ];

      // Create chart configuration
      const config = {
        type: 'line',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{
              id: 'y',
              position: 'left',
              scaleLabel: {
                display: true,
                labelString: 'Drop-off Rate (%)'
              },
              gridLines: {
                drawBorder: false,
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                callback: function(value) {
                  return value + '%';
                },
                beginAtZero: true
              }
            }, {
              id: 'y1',
              position: 'right',
              scaleLabel: {
                display: true,
                labelString: 'Sessions'
              },
              gridLines: {
                drawOnChartArea: false
              },
              ticks: {
                beginAtZero: true
              }
            }],
            xAxes: [{
              gridLines: {
                display: false
              }
            }]
          },
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              boxWidth: 12,
              usePointStyle: true
            }
          },
          tooltips: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            titleFontSize: 13,
            titleFontStyle: 'bold',
            bodyFontSize: 12,
            cornerRadius: 4,
            xPadding: 10,
            yPadding: 10,
            displayColors: true,
            // Don't show tooltip for threshold line
            filter: function(tooltipItem) {
              return tooltipItem.datasetIndex !== 2; // Don't show tooltip for threshold dataset
            }
          }
        }
      };
      
      this.chart = new Chart(ctx, config);
    } catch (error) {
      console.error('Error creating chart:', error);
    }
  }

  private updateChart(): void {
    if (!this.chart || !this.dropoffData) return;
    
    // Prepare data for daily drop-off rates
    const dailyStats = this.dropoffData.dailyStats;
    const dates = Object.keys(dailyStats).sort();
    const displayDates = dates.slice(-14);
    
    const rateData = displayDates.map(date => dailyStats[date].rate);
    const incompleteData = displayDates.map(date => dailyStats[date].incomplete);
    
    this.chart.data.datasets[0].data = rateData;
    this.chart.data.datasets[1].data = incompleteData;
    
    this.chart.update();
  }
}
